import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  FormControlLabel,
  FormHelperText,
  Grid,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { generateCsv, mkConfig, download } from "export-to-csv";
import React, { useCallback, useEffect, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import DropdownComponent from "../../Components/Dropdown/DropdownComponent";
import ConfirmationModal from "../../Components/Modal/Modal";
import Breadcrumb from "../../Components/Navigation/Breadcrumbs/Breadcrumbs";
import Signifier from "../../Components/Signifier";
import { StyledTextField } from "../../Components/TextField/TextField";
import { OutboundQueryTypes } from "../../Enums/OutboundQueryType";
import {
  deleteOutboundFeed,
  editOutboundFeed,
  getAllFeedOptions,
  getStates,
} from "../../Services/API/OutboundFeeds";
import TrackingCodes from "./TrackingCodes";

function OutboundFeedsDetailed() {
  const useStyles = makeStyles((theme) => ({
    root: {
      width: "90%",
      marginBottom: "50px",
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },
    },
    sensitiveSection: {
      width: "100%",
      height: "25%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      paddingTop: "20px",
    },
    sensitiveButton: {
      [theme.breakpoints.down("sm")]: {
        maxWidth: "none",
        width: "100%",
      },
      width: "200px",
    },
    error: {
      color: theme.palette.error.main,
    },
    textField: {
      "& .Mui-error": {
        color: theme.palette.error.main,
      },
      "& .MuiFormHelperText-root": {
        color: theme.palette.error.main,
      },
      "& .MuiInput-underline.Mui-error:after": {
        borderBottomColor: theme.palette.error.main,
      },
    },
    header: {
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        marginTop: "10px",
      },
      display: "flex",
    },
    descLabel: {
      margin: "0px",
      fontWeight: "700",
      marginBottom: "10px",
    },
    descValue: {
      margin: "0px",
      marginLeft: "5px",
      marginRight: "20px",
    },
    descBlock: {
      display: "flex",
    },
    exportButton: {
      backgroundColor: "green",
      color: "white",
      marginRight: "20px",
      marginTop: "20px",
      [theme.breakpoints.down("sm")]: {
        width: "100%",
        marginRight: "10px",
      },
    },
  }));

  const classes = useStyles();

  const location = useLocation();
  const [showInputError, setShowInputError] = useState(false);
  const [showSignifier, setShowSignifier] = useState(false);
  const [showError, setShowError] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [trackingCodes, setTrackingCodes] = useState(
    location.state === undefined
      ? ""
      : location.state.outboundClientFeedTrackingCodes.length === 0
        ? [{ Name: "", Value: "", IsPassThrough: false }]
        : location.state.outboundClientFeedTrackingCodes,
  );
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState([]);
  const [categories, setCategories] = useState([]);
  const [families, setFamilies] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [states, setStates] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState("");
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [name, setName] = useState(location.state === undefined ? "" : location.state.name);
  const [isActive, setIsActive] = useState(location.state === undefined ? "" : location.state.isActive);
  const [useCData, setUseCData] = useState(location.state === undefined ? "" : location.state.useCdata);
  const [keyword, setKeyword] = useState("");
  const [reqNumbers, setReqNumbers] = useState("");
  const [processing, setProcessing] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const csvTitle = name + " Outbound Feeds";

  const csvOptions = mkConfig({
    filename: csvTitle,
    quoteStrings: true,
    showTitle: true,
    title: csvTitle,
    useKeysAsHeaders: true,
  });

  const csvGenerator = generateCsv(csvOptions);

  const createCSV = [
    {
      Name: name,
      Template: selectedTemplate.name,
      Active: isActive,
      CDATA: useCData,
      TrackingCodes: JSON.stringify(trackingCodes, ["name", "value", "isPassThrough"]),
      Country: selectedCountry,
      States: selectedStates,
      Facilities: selectedFacility,
      Brands: selectedBrand,
      Keyword: keyword,
      Requisition: reqNumbers,
    },
  ];
  const handleExportData = () => download(csvOptions)(csvGenerator(createCSV));

  const callAPI = (func, responseMessage) => {
    setProcessing(true);
    func.then((res) => {
      setShowSignifier(true);
      if (res) {
        setResponseMessage(responseMessage);
        setShowError(false);
      } else {
        setShowError(true);
      }
      setProcessing(false);
    });
  };

  const callDelete = async () => {
    await deleteOutboundFeed(location.state.id, location.state.clientId).then((response) => {
      if (response) {
        setDeleted(true);
        return true;
      }
    });
    return true;
  };

  const checkAllEntered = () => {
    if (name === "" || selectedTemplate === "") {
      return false;
    }
    return true;
  };

  const compare = (a, b) => {
    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  };

  const sort = useCallback((array, fieldName) => {
    array.sort((a, b) => {
      const order = compare(a[fieldName], b[fieldName]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
  }, []);

  const prepQueries = () => {
    const retVal = [];
    for (const i in selectedIndustry) {
      const query = {
        QueryTypeID: OutboundQueryTypes.Industry,
        Value: selectedIndustry[i].id,
      };
      retVal.push(query);
    }
    for (const i in selectedFamily) {
      const query = {
        QueryTypeID: OutboundQueryTypes.Family,
        Value: selectedFamily[i].id,
      };
      retVal.push(query);
    }
    for (const i in selectedCategories) {
      const query = {
        QueryTypeID: OutboundQueryTypes.Category,
        Value: selectedCategories[i].id,
      };
      retVal.push(query);
    }
    if (selectedCountry !== "" && selectedCountry !== undefined) {
      const query = {
        QueryTypeID: OutboundQueryTypes.Country,
        Value: selectedCountry.code,
      };
      retVal.push(query);
    }
    for (const i in selectedStates) {
      const query = {
        QueryTypeID: OutboundQueryTypes.Location,
        Value: selectedStates[i].abbreviation,
      };
      retVal.push(query);
    }
    if (selectedFacility !== "" && selectedFacility !== undefined) {
      const query = {
        QueryTypeID: OutboundQueryTypes.Facility,
        Value: selectedFacility.id,
      };
      retVal.push(query);
    }
    if (selectedBrand !== "" && selectedBrand !== undefined) {
      const query = {
        QueryTypeID: OutboundQueryTypes.Brand,
        Value: selectedBrand.name,
      };
      retVal.push(query);
    }
    if (keyword !== "" && keyword !== undefined) {
      const query = { QueryTypeID: OutboundQueryTypes.Keyword, Value: keyword };
      retVal.push(query);
    }
    if (reqNumbers !== "" && reqNumbers !== undefined) {
      reqNumbers.split(", ").forEach((req) => {
        const query = {
          QueryTypeID: OutboundQueryTypes.ReqNumbers,
          Value: req,
        };
        retVal.push(query);
      });
    }
    return retVal;
  };

  const handleAddCode = () => {
    if (trackingCodes.length === 5) {
      return;
    }
    const items = Array.from(trackingCodes);
    items.push({ Name: "", Value: "", IsPassThrough: false });

    setTrackingCodes(items);
  };

  useEffect(() => {
    let reqNumberArray;
    if (location.state !== undefined) {
      getAllFeedOptions(location.state.clientId).then((response) => {
        sort(response.outboundTemplates, "name");
        sort(response.categories, "name");
        sort(response.families, "name");
        sort(response.industries, "name");
        sort(response.countries, "name");
        sort(response.facilities, "name");
        setTemplates(response.outboundTemplates);
        setCategories(response.categories);
        setFamilies(response.families);
        setIndustries(response.industries);
        setCountries(response.countries);
        setFacilities(response.facilities);
        const convertToObject = response.brands.map(function (value) {
          return { name: value };
        });
        sort(convertToObject, "name");
        setBrands(convertToObject);
        for (const i in response.outboundTemplates) {
          if (response.outboundTemplates[i].id === location.state.templateId) {
            setSelectedTemplate(response.outboundTemplates[i]);
          }
        }
      });
      reqNumberArray = [];
      for (const i in location.state.outboundClientFeedQueries) {
        if (location.state.outboundClientFeedQueries[i].queryTypeId === OutboundQueryTypes.Keyword) {
          setKeyword(location.state.outboundClientFeedQueries[i].value);
        }
        if (location.state.outboundClientFeedQueries[i].queryTypeId === OutboundQueryTypes.ReqNumbers) {
          reqNumberArray.push(location.state.outboundClientFeedQueries[i].value);
        }
      }
    }
    setReqNumbers(reqNumberArray.join(", "));
  }, [location.state, sort]);

  useEffect(() => {
    for (const i in countries) {
      if (countries[i].name === selectedCountry.name) {
        getStates(countries[i].id).then((response) => {
          sort(response, "name");
          setStates(response);
          return;
        });
      }
    }
  }, [countries, selectedCountry, sort]);

  useEffect(() => {
    if (location.state !== undefined) {
      const retVal = [];
      for (const i in location.state.outboundClientFeedQueries) {
        if (location.state.outboundClientFeedQueries[i].queryTypeId === OutboundQueryTypes.Industry) {
          for (const x in industries) {
            if (industries[x].id === location.state.outboundClientFeedQueries[i].value) {
              retVal.push(industries[x]);
            }
          }
        }
      }
      setSelectedIndustry(retVal);
    }
  }, [industries, location.state]);

  useEffect(() => {
    if (location.state !== undefined) {
      const retVal = [];
      for (const i in location.state.outboundClientFeedQueries) {
        if (location.state.outboundClientFeedQueries[i].queryTypeId === OutboundQueryTypes.Family) {
          for (const x in families) {
            if (families[x].id === location.state.outboundClientFeedQueries[i].value) {
              retVal.push(families[x]);
            }
          }
        }
      }
      setSelectedFamily(retVal);
    }
  }, [families, location.state]);

  useEffect(() => {
    if (location.state !== undefined) {
      const retVal = [];
      for (const i in location.state.outboundClientFeedQueries) {
        if (location.state.outboundClientFeedQueries[i].queryTypeId === OutboundQueryTypes.Category) {
          for (const x in categories) {
            if (categories[x].id === location.state.outboundClientFeedQueries[i].value) {
              retVal.push(categories[x]);
            }
          }
        }
      }
      setSelectedCategories(retVal);
    }
  }, [categories, location.state]);

  useEffect(() => {
    if (location.state !== undefined) {
      for (const i in location.state.outboundClientFeedQueries) {
        if (location.state.outboundClientFeedQueries[i].queryTypeId === OutboundQueryTypes.Country) {
          for (const x in countries) {
            if (countries[x].code === location.state.outboundClientFeedQueries[i].value) {
              setSelectedCountry(countries[x]);
              return;
            }
          }
        }
      }
    }
  }, [countries, location.state]);

  useEffect(() => {
    if (location.state !== undefined) {
      const retVal = [];
      for (const i in location.state.outboundClientFeedQueries) {
        if (location.state.outboundClientFeedQueries[i].queryTypeId === OutboundQueryTypes.Location) {
          for (const x in states) {
            if (states[x].abbreviation === location.state.outboundClientFeedQueries[i].value) {
              retVal.push(states[x]);
            }
          }
        }
      }
      setSelectedStates(retVal);
    }
  }, [location.state, states]);

  useEffect(() => {
    if (location.state !== undefined) {
      for (const i in location.state.outboundClientFeedQueries) {
        if (location.state.outboundClientFeedQueries[i].queryTypeId === OutboundQueryTypes.Facility) {
          for (const x in facilities) {
            if (facilities[x].id === location.state.outboundClientFeedQueries[i].value) {
              setSelectedFacility(facilities[x]);
              return;
            }
          }
        }
      }
    }
  }, [facilities, location.state]);

  useEffect(() => {
    if (location.state !== undefined) {
      for (const i in location.state.outboundClientFeedQueries) {
        if (location.state.outboundClientFeedQueries[i].queryTypeId === OutboundQueryTypes.Brand) {
          for (const x in brands) {
            if (brands[x].name === location.state.outboundClientFeedQueries[i].value) {
              setSelectedBrand(brands[x]);
              return;
            }
          }
        }
      }
    }
  }, [brands, location.state]);

  const data = {
    ID: location.state === undefined ? "" : location.state.id,
    ClientId: location.state === undefined ? "" : location.state.clientId,
    TemplateId: location.state === undefined ? "" : selectedTemplate.id,
    Name: name,
    IsActive: isActive,
    UseCdata: useCData,
    OutboundClientFeedQueries: prepQueries(),
    OutboundClientFeedTrackingCodes: trackingCodes,
  };

  if (location.state === undefined) {
    return <Redirect to="/outboundfeeds" />;
  }

  if (deleted) {
    return <Redirect to="/outboundfeeds" />;
  }

  return (
    <>
      <Signifier
        showSignifier={showSignifier}
        setShowSignifier={setShowSignifier}
        message={responseMessage}
        error={showError}
      />
      <div className="page-container">
        <Breadcrumb />
        <h1 className="page-title">Outbound Feed</h1>
        <div className={classes.header}>
          <div className={classes.descBlock}>
            <p className={classes.descLabel}>API LINK: </p>
            <a
              className={classes.descValue}
              href={`${import.meta.env.VITE_JOB_FEEDS}/api/feeds/${location.state.id}`}
            >
              {`${import.meta.env.VITE_JOB_FEEDS}/api/feeds/${location.state.id}`}
            </a>
          </div>
        </div>
        <div className={classes.root}>
          <Grid className={classes.cardContainer}>
            <Card className={classes.card}>
              <CardContent>
                <Grid container spacing={1}>
                  <Grid xs={12} sm={6} item>
                    <StyledTextField
                      InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                      placeholder="Name"
                      label="Name"
                      variant="standard"
                      fullWidth
                      required
                      value={name}
                      error={showInputError && name === ""}
                      className={classes.textField}
                      onChange={(e) => setName(e.target.value)}
                    />
                    {showInputError && name === "" && (
                      <FormHelperText className={classes.error}>Entry Required</FormHelperText>
                    )}
                  </Grid>
                  <Grid xs={12} sm={6} item>
                    <DropdownComponent
                      currValue={selectedTemplate}
                      items={templates}
                      property="name"
                      title="Template"
                      handleChange={(value) => setSelectedTemplate(value)}
                      multi={false}
                      error={showInputError && selectedTemplate === ""}
                    />
                    {showInputError && selectedTemplate === "" && (
                      <FormHelperText className={classes.error}>Entry Required</FormHelperText>
                    )}
                  </Grid>
                  <Grid xs={12} sm={12} item>
                    <FormControlLabel
                      control={<Checkbox color="primary" checked={isActive} />}
                      onChange={() => setIsActive(!isActive)}
                      label="Active?"
                    />
                  </Grid>
                  <Grid xs={12} sm={12} item>
                    <FormControlLabel
                      control={<Checkbox color="primary" checked={useCData} />}
                      onChange={() => setUseCData(!useCData)}
                      label="CDATA"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormHelperText
                      style={{
                        marginTop: "20px",
                        marginBottom: "-20px",
                        paddingTop: "10px",
                      }}
                    >
                      Tracking Codes
                    </FormHelperText>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <Button
                      style={{ marginTop: "20px" }}
                      type="submit"
                      variant="contained"
                      color="primary"
                      onClick={() => handleAddCode()}
                    >
                      Add Code
                    </Button>
                  </Grid>

                  <TrackingCodes trackingCodes={trackingCodes} setTrackingCodes={setTrackingCodes} />
                  <Grid item xs={12} sm={12}>
                    <FormHelperText style={{ marginTop: "20px", marginBottom: "-20px" }}>
                      Query Builder
                    </FormHelperText>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DropdownComponent
                      currValue={selectedIndustry}
                      items={industries}
                      property="name"
                      title="Industries"
                      handleChange={(value) => setSelectedIndustry(value)}
                      multi={true}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DropdownComponent
                      currValue={selectedFamily}
                      items={families}
                      property="name"
                      title="Families"
                      handleChange={(value) => setSelectedFamily(value)}
                      multi={true}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DropdownComponent
                      currValue={selectedCategories}
                      items={categories}
                      property="name"
                      title="Categories"
                      handleChange={(value) => setSelectedCategories(value)}
                      multi={true}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DropdownComponent
                      currValue={selectedCountry}
                      items={countries}
                      property="name"
                      title="Countries"
                      handleChange={(value) => setSelectedCountry(value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DropdownComponent
                      currValue={selectedStates}
                      items={states}
                      property="name"
                      title="States"
                      handleChange={(value) => setSelectedStates(value)}
                      multi={true}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DropdownComponent
                      currValue={selectedFacility}
                      items={facilities}
                      property="name"
                      title="Facilities"
                      handleChange={(value) => setSelectedFacility(value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DropdownComponent
                      currValue={selectedBrand}
                      items={brands}
                      property="name"
                      title="Brands"
                      handleChange={(value) => setSelectedBrand(value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                      placeholder="Keyword"
                      label="Keyword"
                      variant="standard"
                      fullWidth
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                      placeholder="Requisition Numbers"
                      label="Requisition Numbers"
                      variant="standard"
                      fullWidth
                      value={reqNumbers}
                      onChange={(e) => setReqNumbers(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} container justifyContent="flex-end">
                    <Button
                      className={classes.exportButton}
                      type="submit"
                      variant="contained"
                      onClick={handleExportData}
                    >
                      Export Data
                    </Button>
                    <Button
                      style={{ marginTop: "20px", marginRight: "8px" }}
                      className={classes.sensitiveButton}
                      type="submit"
                      variant="contained"
                      color="primary"
                      onClick={() =>
                        checkAllEntered()
                          ? callAPI(editOutboundFeed(data), "Feed Saved")
                          : setShowInputError(true)
                      }
                    >
                      Save Information
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <div className={classes.sensitiveSection}>
              <Grid item xs={12} sm={12} container justifyContent="space-between">
                <Button
                  className={classes.sensitiveButton}
                  type="submit"
                  variant="contained"
                  color="secondary"
                  onClick={() => setOpenModal(true)}
                >
                  Delete Feed
                </Button>
              </Grid>
            </div>
          </Grid>
        </div>
      </div>
      <Dialog open={openModal} onClose={() => setOpenModal(false)}>
        <ConfirmationModal
          question="Delete this feed permanently?"
          func={() => callAPI(callDelete(), "Feed Deleted")}
          closeModal={() => setOpenModal(false)}
          processing={processing}
        />
      </Dialog>
    </>
  );
}

export default OutboundFeedsDetailed;
